import { useRef, useState } from 'react';
import { Link } from 'react-router';
import type { TagResponse, TeamResponse } from '../api';
import { errorMessage } from '../api/problems';
import { useMyInvitations } from './invitationQueries';
import { useDeleteTag, useUpdateTag, type LibraryFilters } from './queries';
import { LibrarySwitcher, type Library } from './LibrarySwitcher';
import { TagEditor } from './TagEditor';

/** Which section's library this sidebar is describing. Only the wording differs. */
export type LibrarySection = 'scripts' | 'diagrams';

interface LibrarySidebarProps {
  filters: LibraryFilters;
  tags: TagResponse[];
  teams: TeamResponse[];
  library: Library;
  section: LibrarySection;
  onFiltersChange: (filters: LibraryFilters) => void;
  onLibraryChange: (library: Library) => void;
  onManageTeams: () => void;
}

export function LibrarySidebar({
  filters,
  tags,
  teams,
  library,
  section,
  onFiltersChange,
  onLibraryChange,
  onManageTeams,
}: LibrarySidebarProps) {
  const items = section === 'diagrams' ? 'diagrams' : 'snippets';
  const activeTags = new Set(filters.tag ?? []);

  const roleByTeam = new Map(teams.map((team) => [team.id, team.yourRole]));

  /**
   * Your own tags are yours. A team's are shared vocabulary: every member applies them, but only
   * an admin or owner renames, recolours or deletes one, because that is felt across everybody's
   * snippets. Showing the control to a member would only produce a 403.
   */
  function canManage(tag: TagResponse): boolean {
    if (tag.teamId === null) {
      return true;
    }

    const role = roleByTeam.get(tag.teamId);
    return role === 'Admin' || role === 'Owner';
  }

  /**
   * Filters name tags rather than pointing at their ids, and an unknown name makes the whole
   * filter unsatisfiable — so renaming or deleting the tag you are filtering by would empty the
   * library rather than narrow it. Carry the filter across instead.
   */
  function retagFilters(previous: string, next: string | null) {
    if (!activeTags.has(previous)) {
      return;
    }

    const remaining = (filters.tag ?? []).flatMap((name) =>
      name === previous ? (next === null ? [] : [next]) : [name],
    );

    onFiltersChange({ ...filters, tag: remaining.length > 0 ? remaining : undefined });
  }

  function toggleTag(name: string) {
    const next = new Set(activeTags);
    if (next.has(name)) {
      next.delete(name);
    } else {
      next.add(name);
    }

    onFiltersChange({ ...filters, tag: next.size > 0 ? [...next] : undefined });
  }

  const views = [
    { label: section === 'diagrams' ? 'All diagrams' : 'All snippets',
      active: !filters.isFavorite && !filters.isArchived && !filters.deleted,
      apply: () => onFiltersChange({ ...filters, isFavorite: undefined, isArchived: undefined, deleted: undefined }) },
    { label: 'Favourites', active: filters.isFavorite === true,
      apply: () => onFiltersChange({ ...filters, isFavorite: true, isArchived: undefined, deleted: undefined }) },
    { label: 'Archived', active: filters.isArchived === true,
      apply: () => onFiltersChange({ ...filters, isArchived: true, isFavorite: undefined, deleted: undefined }) },
    { label: 'Trash', active: filters.deleted === true,
      apply: () => onFiltersChange({ ...filters, deleted: true, isFavorite: undefined, isArchived: undefined }) },
  ];

  return (
    <nav
      aria-label="Library"
      className="flex h-full w-full flex-col gap-4 overflow-y-auto border-r border-border-subtle
        bg-surface-sunken px-3 py-4 md:w-56"
    >
      <LibrarySwitcher
        library={library}
        teams={teams}
        onChange={onLibraryChange}
        onManageTeams={onManageTeams}
      />

      <InvitationsNotice />

      <ul className="flex flex-col gap-0.5">
        {views.map((view) => (
          <li key={view.label}>
            <button
              type="button"
              aria-current={view.active ? 'page' : undefined}
              onClick={view.apply}
              className={`w-full rounded-md px-2 py-1.5 text-left text-sm transition-colors
                ${view.active ? 'bg-accent-subtle text-text' : 'text-text-muted hover:text-text'}`}
            >
              {view.label}
            </button>
          </li>
        ))}
      </ul>

      <div className="flex flex-col gap-1">
        <h2 className="px-2 text-xs font-medium tracking-wide text-text-faint uppercase">Tags</h2>

        {tags.length === 0 ? (
          <p className="px-2 text-xs text-text-faint">
            No tags yet. Add one from a {section === 'diagrams' ? 'diagram' : 'snippet'}.
          </p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {tags.map((tag) => (
              <TagRow
                key={tag.id}
                tag={tag}
                active={activeTags.has(tag.name)}
                canManage={canManage(tag)}
                onToggle={() => toggleTag(tag.name)}
                onRetag={retagFilters}
              />
            ))}
          </ul>
        )}

        {activeTags.size > 1 && (
          <p className="px-2 pt-1 text-xs text-text-faint">
            Showing {items} with all {activeTags.size} tags.
          </p>
        )}
      </div>
    </nav>
  );
}

interface TagRowProps {
  tag: TagResponse;
  active: boolean;
  canManage: boolean;
  onToggle: () => void;
  /** Follows an active filter through a rename, or drops it on a delete. Null means deleted. */
  onRetag: (previous: string, next: string | null) => void;
}

/**
 * One tag in the sidebar: a filter toggle, and — for a tag you may manage — an editor behind a
 * "⋯". Its own component so the row owns the element that bounds the popover, and so each row's
 * mutation errors belong to that row rather than to the sidebar.
 */
function TagRow({ tag, active, canManage, onToggle, onRetag }: TagRowProps) {
  const [open, setOpen] = useState(false);
  const rowRef = useRef<HTMLLIElement>(null);

  const updateTag = useUpdateTag();
  const deleteTag = useDeleteTag();

  function close() {
    setOpen(false);
    updateTag.reset();
    deleteTag.reset();
  }

  return (
    <li ref={rowRef} className="group relative flex items-center">
      <button
        type="button"
        aria-pressed={active}
        onClick={onToggle}
        className={`flex min-w-0 flex-1 items-center justify-between gap-2 rounded-md px-2 py-1
          text-left text-sm transition-colors
          ${active ? 'bg-accent-subtle text-text' : 'text-text-muted hover:text-text'}`}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          {tag.color && (
            <span
              aria-hidden="true"
              className="size-2 shrink-0 rounded-full"
              style={{ backgroundColor: tag.color }}
            />
          )}
          <span className="truncate">{tag.name}</span>
        </span>
        <span className="shrink-0 text-xs text-text-faint">{tag.snippetCount}</span>
      </button>

      {canManage && (
        // The slot is always present, so revealing the button on hover cannot shove the count
        // sideways. Focus reveals it too, or it would be unreachable from the keyboard.
        <button
          type="button"
          aria-label={`Edit tag ${tag.name}`}
          aria-expanded={open}
          onClick={() => (open ? close() : setOpen(true))}
          className="ml-0.5 shrink-0 rounded px-1 text-sm leading-none text-text-faint opacity-0
            transition-opacity group-hover:opacity-100 hover:text-text focus-visible:opacity-100
            aria-expanded:opacity-100"
        >
          ⋯
        </button>
      )}

      {open && (
        <TagEditor
          tag={tag}
          anchorRef={rowRef}
          busy={updateTag.isPending || deleteTag.isPending}
          error={
            updateTag.isError
              ? errorMessage(updateTag.error)
              : deleteTag.isError
                ? errorMessage(deleteTag.error)
                : undefined
          }
          onSave={(next) =>
            updateTag.mutate(
              { id: tag.id, ...next },
              {
                // A colour click leaves the editor open, so trying another is one click away. A
                // rename is a finished thought, and the row behind now says so.
                onSuccess: (saved) => {
                  if (saved.name !== tag.name) {
                    onRetag(tag.name, saved.name);
                    close();
                  }
                },
              },
            )
          }
          onDelete={() =>
            deleteTag.mutate(tag.id, {
              onSuccess: () => {
                onRetag(tag.name, null);
                close();
              },
            })
          }
          onClose={close}
        />
      )}
    </li>
  );
}

/**
 * A quiet pointer to anything waiting for a decision.
 *
 * The invitation emails link to `/invitations`, but somebody who is already signed in and reading
 * the app should not have to go back to their inbox to find out that a library is being offered
 * to them. Renders nothing at all when there is nothing waiting.
 */
function InvitationsNotice() {
  const invitations = useMyInvitations();
  const waiting = invitations.data?.length ?? 0;

  if (waiting === 0) {
    return null;
  }

  return (
    <Link
      to="/invitations"
      className="rounded-md bg-accent-subtle px-2 py-1.5 text-xs text-text
        transition-colors hover:brightness-110"
    >
      {waiting === 1 ? '1 invitation waiting' : `${waiting} invitations waiting`}
    </Link>
  );
}
